import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'store'
import { postAPI } from 'utils/apiUtil'
import { createCSV } from 'utils/fileUtil'
import { buttonInfo, voidF } from 'types/FunctionTypes'
import { CSVJuryo } from 'assets/CSVHeaders'
import { commonMsg } from 'assets/MessageCode'
import * as PageAction from 'slicers/PageSlice'
import * as TableJuryoAction from 'slicers/TableSlicers/TableJuryoSlice'
import { getRequestJoken, getRequestJokenPDF, getRequestJuryoUpdate, requestJuryo, requestJuryoPDF, requestJuryoUpdate, responseJuryo, responseJuryoPDF, responseJuryoUpdate } from 'utils/requestUtil'
import { csvInfo } from 'types/SearchTypes'
import { useMediaQueryContext } from 'MediaQueryProvider'
import { getDenpyoKbnName } from 'utils/stringUtil'
import { exportReport } from 'utils/reportServiceUtil'
import { juryoCSV } from 'types/FileTypes'

// サマリチェックリスト //================================//
type check = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLTableRowElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>, isChecked: boolean) => void
export const useCheckListSummary = (jokenList: responseJuryo[]): [responseJuryo[], check] => {
  const dispatch = useDispatch()
  const selectList = useSelector((state) => state.TableJuryoReducer.selectList)
  const allClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLTableRowElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>, isChecked: boolean) => {
      e.preventDefault()
      if (jokenList.length > 0 && !isChecked && !window.confirm('全選択されます。よろしいでしょうか？')) {
        // キャンセル
        return
      }
      if (jokenList.length > 0 && isChecked && !window.confirm('全選択が解除されます。よろしいでしょうか？')) {
        // キャンセル
        return
      }
      dispatch(TableJuryoAction.checkAllJoken({ isChecked: isChecked, jokenList: jokenList }))
    },
    [jokenList],
  )
  return [selectList, allClick]
}

/**
 * ボタン押下処理(CSV出力、PDF出力、受領、内容確認依頼)
 * @returns
 */
export const useButtons = (denpyoList: responseJuryo[], allCount: number, csvInfo: csvInfo, refetch: voidF): buttonInfo[] => {
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.PageReducer.loading)
  const isJapell = useSelector((state) => state.LoginReducer.isJapell)
  const MAIL = useSelector((state) => state.LoginReducer.MAIL)
  const keyInfo = useSelector((state) => state.LoginReducer.keyInfo)
  const selectList = useSelector((state) => state.TableJuryoReducer.selectList)
  const searchJoken = useSelector((state) => state.SearchJuryoReducer.searchJoken)
  const page = useSelector((state) => state.TableReducer.page)
  const perPage = useSelector((state) => state.TableReducer.perPage)
  const { isMobileSite } = useMediaQueryContext()

  const changeErrMsg = () => dispatch(PageAction.changeMessageError(commonMsg.ERROR_MSG)) //
  const loaded = () => dispatch(PageAction.endLoading()) //

  // ボタン活性状況 //================================//
  // selectListに自分の店舗のデータが含まれているかチェック
  const isSelectedTantoTenData = (() => {
    if (selectList.length <= 0) return false
    for (let i = 0; i < selectList.length; i++) {
      const data = selectList[i]
      if (
        !keyInfo.some((r) => {
          return r.TOKUI_CD === data.TOKUI_CD && r.TEN_CD === data.TEN_CD
        })
      ) {
        // 担当店舗以外のデータの場合
        return false
      }
    }
    return true
  })()
  const disabled1 = !isSelectedTantoTenData // 1つ目のボタンの活性状況(受領)
  const disabled2 = !isSelectedTantoTenData // 2つ目のボタンの活性状況(内容確認依頼)
  const isAll = selectList.length === 0 || selectList.length === denpyoList.length // チェックを付けていない、またはすべてにチェックを付けているか

  // 全選択ボタン押下処理 //================================//
  const clickZenSentaku = useCallback(
    (_e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      // e.preventDefault()
      if (selectList.length !== denpyoList.length && !window.confirm('全選択されます。よろしいでしょうか？')) {
        // キャンセル
        return
      }
      if (selectList.length === denpyoList.length && !window.confirm('全選択が解除されます。よろしいでしょうか？')) {
        // キャンセル
        return
      }
      dispatch(TableJuryoAction.checkAllJoken({ isChecked: selectList.length === denpyoList.length, jokenList: denpyoList }))
    },
    [denpyoList, selectList],
  )

  // 受領ボタン押下処理 //================================//
  const clickJuryo = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      if (!loading) {
        // ローディング開始
        dispatch(PageAction.startLoading('処理中...'))
        await postAPI<requestJuryoUpdate, responseJuryoUpdate>(
          '/postJuryo',
          getRequestJuryoUpdate(MAIL, keyInfo, selectList),
          (r) => {
            dispatch(PageAction.changeMessage(r))
            if (!r.error) {
              // 検索後メッセージを残すためにフラグを更新
              dispatch(PageAction.changeIsNotDeleteMessage(true))
              refetch()
            }
          },
          changeErrMsg,
          loaded,
        )
      }
    },
    [loading, selectList],
  )

  // 内容確認依頼ボタン押下処理 //================================//
  const clickIrai = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      if (!loading) {
        // ローディング開始
        dispatch(PageAction.startLoading('処理中...'))
        await postAPI<requestJuryoUpdate, responseJuryoUpdate>(
          '/postNaiyo',
          getRequestJuryoUpdate(MAIL, keyInfo, selectList),
          (r) => {
            dispatch(PageAction.changeMessage(r))
            if (!r.error) {
              // 検索後メッセージを残すためにフラグを更新
              dispatch(PageAction.changeIsNotDeleteMessage(true))
              refetch()
            }
          },
          changeErrMsg,
          loaded,
        )
      }
    },
    [loading, selectList],
  )

  // CSVボタン押下処理 //================================//
  const clickCSV = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      if (!loading) {
        dispatch(PageAction.deleteMessage())
        const dataList: responseJuryo[] = []
        if (isJapell && perPage < allCount && isAll) {
          // ジャペルは表示ページ分しかデータを持っていないので全CSV出力で一ページの表示件数分以上件数がある場合、バックエンドからCSV出力データを取得
          // ローディング開始
          dispatch(PageAction.startLoading('処理中...'))
          await postAPI<requestJuryo, { table: responseJuryo[] }>(
            '/postJuryoHyoInfo',
            getRequestJoken(isJapell, true, keyInfo, searchJoken, page, perPage),
            (r) => {
              if (!r.error) {
                // エラーがない場合
                r.body.table.forEach((r) => {
                  dataList.push({ ...r })
                })
              } else {
                // エラーがある場合、メッセージを表示して終了
                dispatch(PageAction.changeMessage(r))
                return
              }
            },
            changeErrMsg,
            loaded,
          )
          // データがない場合、処理を終了
          if (dataList.length === 0) return
        } else {
          // フロントエンドからCSV出力データを取得。確認内容依頼は画面の入力値を出力するので全件選択していてもselectListを出す。
          ;(selectList.length > 0 ? selectList : denpyoList).forEach((r) => {
            dataList.push({ ...r })
          })
        }

        const csvData: juryoCSV[] = dataList.map((r) => {
          return {
            ...r,
            DENPYO_KBN: getDenpyoKbnName(r.DENPYO_KBN),
            GENKA: r.GEN_TANKA * r.NOHIN_SURYO,
            BAIKA: r.BAI_TANKA * r.NOHIN_SURYO,
            ERROR: '',
          }
        })
        createCSV<juryoCSV>(CSVJuryo, csvData, csvInfo.filename)
        dispatch(PageAction.changeMessageSuccess(csvInfo.message))
      }
    },
    [loading, allCount, isAll, isJapell, keyInfo, searchJoken, page, perPage, selectList, denpyoList],
  )

  // PDFボタン押下処理 //================================//
  const clickPDF = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      if (!loading) {
        dispatch(PageAction.deleteMessage())
        const dataList: responseJuryo[] = isAll ? denpyoList : selectList

        // ローディング開始
        dispatch(PageAction.startLoading('処理中...'))
        if (isJapell && perPage < allCount && isAll) {
          // ページネーション表示かつ全選択の場合、全明細取得してからPDFデータ作成する
          await postAPI<requestJuryo, { table: responseJuryo[] }>(
            '/postJuryoHyoInfo',
            getRequestJoken(isJapell, true, keyInfo, searchJoken, page, perPage),
            (r) => {
              if (!r.error) {
                // エラーがない場合、取得全明細を使ってPDFデータ作成
                postAPI<requestJuryoPDF, responseJuryoPDF>('/postJuryoPDF', getRequestJokenPDF(r.body.table), (r) => {
                  if (!r.error) {
                    // PDF出力処理
                    exportReport(r.body.result).then((result: { data: Blob; download: (filename?: string | undefined) => void }) => {
                      const pdfInfo = r.body.pdfInfo
                      result.download(pdfInfo.name)
                      dispatch(PageAction.changeMessageSuccess(pdfInfo.message))
                    })
                  } else {
                    // エラーがある場合
                    dispatch(PageAction.changeMessage(r))
                  }
                })
              } else {
                // エラーがある場合、メッセージを表示して終了
                dispatch(PageAction.changeMessage(r))
              }
            },
            changeErrMsg,
            loaded,
          )
        } else {
          // 全選択してない場合、選択明細でPDFデータ作成
          await postAPI<requestJuryoPDF, responseJuryoPDF>(
            '/postJuryoPDF',
            getRequestJokenPDF(dataList),
            (r) => {
              if (!r.error) {
                // PDF出力処理を作成
                exportReport(r.body.result).then((result: { data: Blob; download: (filename?: string | undefined) => void }) => {
                  const pdfInfo = r.body.pdfInfo
                  result.download(pdfInfo.name)
                  dispatch(PageAction.changeMessageSuccess(pdfInfo.message))
                })
              } else {
                // エラーがある場合
                dispatch(PageAction.changeMessage(r))
              }
            },
            changeErrMsg,
            loaded,
          )
        }
      }
    },
    [loading, isAll, selectList, denpyoList],
  )

  const ret: buttonInfo[] = []
  if (isJapell) {
    ret.push({
      disabled: denpyoList.length === 0,
      label: isAll ? '全CSV出力' : 'CSV出力',
      click: clickCSV,
    })
  }

  if (!isJapell) {
    if (!isMobileSite) {
      ret.push(
        {
          disabled: denpyoList.length === 0,
          label: isAll ? '全CSV出力' : 'CSV出力',
          click: clickCSV,
        },
        {
          disabled: denpyoList.length === 0,
          label: isAll ? '全PDF出力' : 'PDF出力',
          click: clickPDF,
        },
      )
    }

    ret.push(
      {
        disabled: denpyoList.length === 0,
        label: selectList.length > 0 && selectList.length === denpyoList.length ? '全選択解除' : '全選択',
        click: clickZenSentaku,
      },
      {
        disabled: disabled1,
        label: '受領',
        click: clickJuryo,
      },
      {
        disabled: disabled2,
        label: '内容確認依頼',
        alert: true,
        click: clickIrai,
      },
    )
  }

  return ret
}

// 検索後フォーマット処理 //================================//
export const useSearchedFormat = (): [responseJuryo[], voidF] => {
  const dispatch = useDispatch()
  const selectList = useSelector((state) => state.TableJuryoReducer.selectList)
  const format = () => {
    dispatch(TableJuryoAction.formatCheckSummary())
  }
  return [selectList, format]
}
